/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable, OnDestroy } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { FacUserService, UserInfo } from '@foglight/angular-common';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { isCloudFlagSelector, userInfo } from 'src/app/store/app.selectors';
import permissions from '../../../assets/json/permissions.json';
import { appModuleActions } from 'src/app/views/users-management/store/action-types';
import { isEmpty } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})
export class RoleGuard implements CanActivate, OnDestroy {
  private destroy$ = new Subject<void>();
  unauthorizedMessage = '';
  constructor(
    private router: Router,
    private userService: FacUserService,
    private store: Store
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.store.select(userInfo).pipe(
      switchMap((userInfo: UserInfo) => {
        if (!isEmpty(userInfo)) {
          return of(this.roleVerification(userInfo, route));
        } else {
          return this.userService.getUserInfo().pipe(
            map((userInfo) => {
              this.store.dispatch(
                appModuleActions.setUserInfo({ userInfo: userInfo })
              );
              return this.roleVerification(userInfo, route);
            })
          );
        }
      })
    );
  }

  roleVerification(userInfo: UserInfo, route: ActivatedRouteSnapshot): boolean {
    let canLoad = false;
    const roles = userInfo.roles || [];
    //notification-management permissions
    if (route.url.toString().includes('notification-management')) {
      permissions['notification-management'].forEach((role) => {
        if (roles.includes(role)) {
          canLoad = true;
        }
      });
      if (!canLoad) {
        this.unauthorizedMessage =
          'Accessing notification channels requires Alarm Management Administrator role.';
        sessionStorage.setItem('lastErrorMessage', this.unauthorizedMessage);
        this.router.navigateByUrl('unauthorized');
      }
    }

    // alarm-template permissions
    if (route.url.toString().includes('alarm-template')) {
      permissions['alarm-template'].forEach((role) => {
        if (roles.includes(role)) {
          canLoad = true;
        }
      });
      if (!canLoad) {
        this.unauthorizedMessage =
          'Accessing alarm template requires Alarm Management Administrator role.';
        sessionStorage.setItem('lastErrorMessage', this.unauthorizedMessage);
        this.router.navigateByUrl('unauthorized');
      }
    }

    // Audit Log permissions
    if (route.url.toString().includes('audit-log')) {
      permissions['audit-log'].forEach((role) => {
        if (roles.includes(role)) {
          canLoad = true;
        }
      });
      if (!canLoad) {
        this.unauthorizedMessage =
          'Accessing Audit Log requires Monitoring Administrator role.';
        sessionStorage.setItem('lastErrorMessage', this.unauthorizedMessage);
        this.router.navigateByUrl('unauthorized');
      }
    }

    // users management permissions
    if (route.url.toString().includes('manage-users')) {
      permissions['users-management'].forEach((role) => {
        if (roles.includes(role)) {
          canLoad = true;
        }
      });
      if (!canLoad) {
        this.unauthorizedMessage =
          'You do not have the required role for users management.';
        sessionStorage.setItem('lastErrorMessage', this.unauthorizedMessage);
        this.router.navigateByUrl('unauthorized');
      }
    }

    // integration-hub permissions
    if (route.url.toString().includes('integration-hub')) {
      permissions['integration-hub'].forEach((role) => {
        if (roles.includes(role)) {
          canLoad = true;
        }
      });
      if (!canLoad) {
        const requiredRoles = permissions['integration-hub'];
        this.unauthorizedMessage =
          `Accessing Integration Hub requires ${requiredRoles} role.`;
        sessionStorage.setItem('lastErrorMessage', this.unauthorizedMessage);
        this.router.navigateByUrl('unauthorized');
      }
    }

    // admin permissions
    this.store
      .select(isCloudFlagSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCloud) => {
        if (!isCloud) {
          canLoad = true;
        } else {
          if (route.url.toString().includes('system-aui-administration')) {
            permissions['saas-acceptance'].forEach((role) => {
              if (roles.includes(role)) {
                canLoad = true;
              }
            });
            if (!canLoad) {
              this.unauthorizedMessage =
                'You do not have the required roles to access this page';
              sessionStorage.setItem(
                'lastErrorMessage',
                this.unauthorizedMessage
              );
              this.router.navigateByUrl('unauthorized');
            }
          }
        }
      });

    return canLoad;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
